import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import DOMPurify from 'dompurify';
import bcrypt from 'bcryptjs';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { IoEyeOutline, IoEyeOffOutline, IoCheckmarkCircle, IoCloseCircle, IoLockClosed, IoShieldCheckmark } from 'react-icons/io5';
import { MdInfo } from 'react-icons/md';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './ChangePasswordPage.scss';

export default function ChangePasswordPage(props) {

    const className = 'changePasswordPage';
    const userAuthService = new UserAuthService();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [rawNewPassword, setRawNewPassword] = useState('');

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [invalidCurrentPassword] = useState(false);
    const [incorrectCurrentPassword, setIncorrectCurrentPassword] = useState(false);
    const [invalidNewPassword, setInvalidNewPassword] = useState(false);
    const [sameNewPassword, setSameNewPassword] = useState(false);
    const [invalidConfirmPassword, setInvalidConfirmPassword] = useState(false);

    const [invalidAttempt, setInvalidAttempt] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { activeUserId } = useReduxActiveUser();
    const username = activeUserId.userName;

    const verifyCurrentPassword = async (password) => {
        const token = await userAuthService.verifyPassword({ username, password });
        if (token.body === 'true') {
            return true;
        }
        return false;
    };

    function checkEqualPasswords(newPassword, confirmPassword) {
        return confirmPassword === newPassword;
    }

    function checkNewPassword(currentPassword, newPassword) {
        return !(currentPassword === newPassword);
    }

    function passwordStrengthCheck(newPassword) {
        if ((newPassword != null && newPassword.length < 8) || newPassword === null) {
            return false;
        } else {
            return true;
        }
    }

    const getPasswordStrength = (password) => {
        if (!password) return 0;
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 12.5;
        if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5;
        return Math.min(strength, 100);
    };

    const getStrengthColor = (strength) => {
        if (strength < 25) return '#ef4444';
        if (strength < 50) return '#f59e0b';
        if (strength < 75) return '#eab308';
        return '#22c55e';
    };

    const getStrengthLabel = (strength) => {
        if (strength < 25) return 'Weak';
        if (strength < 50) return 'Fair';
        if (strength < 75) return 'Good';
        return 'Strong';
    };

    const passwordStrength = getPasswordStrength(rawNewPassword);
    const strengthColor = getStrengthColor(passwordStrength);
    const strengthLabel = getStrengthLabel(passwordStrength);

    const passwordRequirements = [
        { label: 'At least 8 characters', met: rawNewPassword.length >= 8 },
        { label: 'Contains uppercase & lowercase', met: /[a-z]/.test(rawNewPassword) && /[A-Z]/.test(rawNewPassword) },
        { label: 'Contains a number', met: /[0-9]/.test(rawNewPassword) },
        { label: 'Contains a special character', met: /[^a-zA-Z0-9]/.test(rawNewPassword) }
    ];

    const currentPasswordChangeHandler = async (e) => {
        let sanitizedCurrentPassword = DOMPurify.sanitize(e.target.value);
        let salt = '$2a$10$CwTycUXWue0Thq9StjUM0u';
        let hashedPassword = bcrypt.hashSync(sanitizedCurrentPassword, salt);
        setCurrentPassword(hashedPassword);
        setIncorrectCurrentPassword(false);
        setInvalidAttempt(false);
        setPasswordChanged(false);
    };

    const newPasswordChangeHandler = async (e) => {
        let sanitizedNewPassword = DOMPurify.sanitize(e.target.value);
        setRawNewPassword(sanitizedNewPassword);
        let salt = '$2a$10$CwTycUXWue0Thq9StjUM0u';
        let hashedPassword = bcrypt.hashSync(sanitizedNewPassword, salt);
        setNewPassword(hashedPassword);
        let validNewPassword = passwordStrengthCheck(sanitizedNewPassword);
        setInvalidNewPassword(!validNewPassword);
        setInvalidAttempt(false);
        setPasswordChanged(false);
    };

    const confirmNewPasswordChangeHandler = async (e) => {
        let sanitizedConfirmNewPassword = DOMPurify.sanitize(e.target.value);
        let salt = '$2a$10$CwTycUXWue0Thq9StjUM0u';
        let hashedPassword = bcrypt.hashSync(sanitizedConfirmNewPassword, salt);
        setConfirmPassword(hashedPassword);
        let validConfirmPassword = checkEqualPasswords(newPassword, hashedPassword);
        setInvalidConfirmPassword(!validConfirmPassword);
        setInvalidAttempt(false);
        setPasswordChanged(false);
    };

    const handleSave = async (e) => {
        try {
            e?.preventDefault();
            setIsSubmitting(true);

            if (!newPassword || !confirmPassword) {
                setInvalidAttempt(true);
                setPasswordChanged(false);
            } else {
                let correctCurrentPassword = await verifyCurrentPassword(currentPassword);
                setIncorrectCurrentPassword(!correctCurrentPassword);
                setSameNewPassword(!(await checkNewPassword(currentPassword, newPassword)));
                let validConfirmPassword = await checkEqualPasswords(newPassword, confirmPassword);
                setInvalidConfirmPassword(!validConfirmPassword);

                if (!correctCurrentPassword || invalidNewPassword || sameNewPassword || !validConfirmPassword) {
                    setInvalidAttempt(true);
                    setPasswordChanged(false);
                } else {
                    const token = await userAuthService.updatePassword({
                        userId: activeUserId,
                        password: newPassword,
                    });
                    if (token.token === 'void') {
                        setInvalidAttempt(true);
                        setPasswordChanged(false);
                    } else {
                        setInvalidAttempt(false);
                        setPasswordChanged(true);
                        // Reset form after success
                        setTimeout(() => {
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                            setRawNewPassword('');
                        }, 2000);
                    }
                }
            }
        } catch (err) {
            setInvalidAttempt(true);
            setPasswordChanged(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={className}>
            <div className={`${className}__container`}>
                <div className={`${className}__header`}>
                    <div className={`${className}__header__icon`}>
                        <IoLockClosed />
                    </div>
                    <div className={`${className}__header__content`}>
                        <h2 className={`${className}__title`}>Change Password</h2>
                        <p className={`${className}__subtitle`}>
                            Update your password to keep your account secure
                        </p>
                    </div>
                </div>

                <form className={`${className}__form`} onSubmit={handleSave}>

                    {/* ── Current Password ─────────────────────────── */}
                    <div className={`${className}__section`}>
                        <div className={`${className}__section__head`}>
                            <IoLockClosed />
                            <h3>Current Password</h3>
                        </div>
                        <div className={`${className}__field`}>
                            <label className={`${className}__label`}>Enter your current password</label>
                            <TextField
                                placeholder='Current password'
                                className={`${className}__input`}
                                type={showCurrentPassword ? 'text' : 'password'}
                                onChange={currentPasswordChangeHandler}
                                variant='outlined'
                                fullWidth
                                error={incorrectCurrentPassword}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IoLockClosed className={`${className}__input__icon`} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                edge="end"
                                            >
                                                {showCurrentPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '10px',
                                        backgroundColor: 'var(--color-muiBackground)'
                                    }
                                }}
                            />
                            {incorrectCurrentPassword && (
                                <div className={`${className}__error`}>
                                    <IoCloseCircle />
                                    <span>The current password you entered is incorrect</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── New Password ─────────────────────────────── */}
                    <div className={`${className}__section`}>
                        <div className={`${className}__section__head`}>
                            <IoShieldCheckmark />
                            <h3>New Password</h3>
                        </div>
                        <div className={`${className}__field`}>
                        <label className={`${className}__label`}>Enter your new password</label>
                        <TextField
                            placeholder='New password'
                            className={`${className}__input`}
                            type={showNewPassword ? 'text' : 'password'}
                            onChange={newPasswordChangeHandler}
                            variant='outlined'
                            fullWidth
                            error={invalidNewPassword || sameNewPassword}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoShieldCheckmark className={`${className}__input__icon`} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            edge="end"
                                        >
                                            {showNewPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: '10px',
                                    backgroundColor: 'var(--color-muiBackground)'
                                }
                            }}
                        />

                        {/* Password Strength Indicator */}
                        {rawNewPassword && (
                            <div className={`${className}__strength`}>
                                <div className={`${className}__strength__header`}>
                                    <span className={`${className}__strength__label`}>Password Strength:</span>
                                    <span
                                        className={`${className}__strength__value`}
                                        style={{ color: strengthColor }}
                                    >
                                        {strengthLabel}
                                    </span>
                                </div>
                                <LinearProgress
                                    variant="determinate"
                                    value={passwordStrength}
                                    className={`${className}__strength__bar`}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        backgroundColor: 'var(--color-primaryGrey)',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: strengthColor,
                                            borderRadius: 3
                                        }
                                    }}
                                />
                            </div>
                        )}

                        {/* Password Requirements */}
                        {rawNewPassword && (
                            <div className={`${className}__requirements`}>
                                <div className={`${className}__requirements__header`}>
                                    <MdInfo />
                                    <span>Password Requirements</span>
                                </div>
                                <ul className={`${className}__requirements__list`}>
                                    {passwordRequirements.map((req, index) => (
                                        <li
                                            key={index}
                                            className={`${className}__requirements__item ${
                                                req.met ? `${className}__requirements__item--met` : ''
                                            }`}
                                        >
                                            {req.met ? (
                                                <IoCheckmarkCircle className={`${className}__requirements__icon--success`} />
                                            ) : (
                                                <IoCloseCircle className={`${className}__requirements__icon--error`} />
                                            )}
                                            <span>{req.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {sameNewPassword && (
                            <div className={`${className}__error`}>
                                <IoCloseCircle />
                                <span>New password must be different from current password</span>
                            </div>
                        )}
                        </div>
                    </div>

                    {/* ── Confirm Password ─────────────────────────── */}
                    <div className={`${className}__section`}>
                        <div className={`${className}__section__head`}>
                            <IoShieldCheckmark />
                            <h3>Confirm New Password</h3>
                        </div>
                        <div className={`${className}__field`}>
                        <label className={`${className}__label`}>Re-enter your new password</label>
                        <TextField
                            placeholder='Re-enter your new password'
                            className={`${className}__input`}
                            type={showConfirmPassword ? 'text' : 'password'}
                            onChange={confirmNewPasswordChangeHandler}
                            variant='outlined'
                            fullWidth
                            error={invalidConfirmPassword}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IoShieldCheckmark className={`${className}__input__icon`} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: '10px',
                                    backgroundColor: 'var(--color-muiBackground)'
                                }
                            }}
                        />
                        {invalidConfirmPassword && confirmPassword && (
                            <div className={`${className}__error`}>
                                <IoCloseCircle />
                                <span>Passwords do not match</span>
                            </div>
                        )}
                        {!invalidConfirmPassword && confirmPassword && newPassword && (
                            <div className={`${className}__success`}>
                                <IoCheckmarkCircle />
                                <span>Passwords match</span>
                            </div>
                        )}
                    </div>
                    </div>

                    {/* Status Messages */}
                    {invalidAttempt && (
                        <div className={`${className}__alert ${className}__alert--error`}>
                            <IoCloseCircle />
                            <div>
                                <strong>Password Change Failed</strong>
                                <p>Please check the errors above and try again</p>
                            </div>
                        </div>
                    )}

                    {passwordChanged && (
                        <div className={`${className}__alert ${className}__alert--success`}>
                            <IoCheckmarkCircle />
                            <div>
                                <strong>Password Changed Successfully!</strong>
                                <p>Your password has been updated. Use your new password for future logins.</p>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className={`${className}__actions`}>
                        <button
                            type="submit"
                            className={`${className}__submit`}
                            disabled={
                                newPassword.length === 0 ||
                                confirmPassword.length === 0 ||
                                invalidConfirmPassword ||
                                invalidCurrentPassword ||
                                invalidNewPassword ||
                                isSubmitting
                            }
                        >
                            {isSubmitting ? (
                                <>
                                    <div className={`${className}__spinner`}></div>
                                    Changing Password...
                                </>
                            ) : (
                                <>
                                    <IoShieldCheckmark />
                                    Change Password
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
